import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FormService } from '../../form/form.service';
import { ITickmark } from '../range/range.component';

export interface IMultiRangeOutput {
    from: number;
    to: number;
}

@Component({
    selector: 'app-input-multi-range',
    template: `
        <div class="multi-range">
            <form *ngIf="form" [formGroup]="form" (keyup.enter)="onSubmit()" (ngSubmit)="onSubmit()">
                <div class="multi-range__input-wrapper">
                    <app-input
                        controlName="rangeInputLowestValue"
                        cssClasses="multi-range__input"
                        [inputMode]="'decimal'"
                        [parentForm]="form"
                    ></app-input>

                    <span class="multi-range__input-seperator">-</span>

                    <app-input
                        controlName="rangeInputHighestValue"
                        cssClasses="multi-range__input"
                        [inputMode]="'decimal'"
                        [parentForm]="form"
                    ></app-input>
                </div>

                <div class="multi-range__range-wrapper">
                    <app-input-range
                        class="multi-range__range-input multi-range__range-input--bottom"
                        controlName="rangeOne"
                        [parentForm]="form"
                        [minVal]="minVal"
                        [maxVal]="maxVal"
                        [hideRangeMarks]="true"
                        [controlledTrackStart]="trackProgressPercentageStart"
                        [controlledTrackEnd]="trackProgressPercentageEnd"
                        (handleValueChange)="handleRangeSubmit()"
                        (liveOnChangeValueUseWithCautionTriggersOnEverySingleValueChange)="handleRangeChange()"
                        cssClasses="input__input-element--white-background"
                        [inputStepSize]="inputStepSize"
                    ></app-input-range>

                    <app-input-range
                        class="multi-range__range-input multi-range__range-input--top"
                        controlName="rangeTwo"
                        [parentForm]="form"
                        [minVal]="minVal"
                        [maxVal]="maxVal"
                        [hideRangeMarks]="true"
                        (handleValueChange)="handleRangeSubmit()"
                        (liveOnChangeValueUseWithCautionTriggersOnEverySingleValueChange)="handleRangeChange()"
                        cssClasses="input__input-element--white-background"
                        [inputStepSize]="inputStepSize"
                    ></app-input-range>
                </div>
            </form>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputMultiRangeComponent implements OnChanges, OnInit, OnDestroy {
    @Input() inputStepSize = 1000;
    @Input() minVal: number;
    @Input() maxVal: number;
    @Input() controlledValStart?: number;
    @Input() controlledValEnd?: number;
    @Output() handleChange = new EventEmitter<IMultiRangeOutput>();

    trackProgressPercentageStart?: string;
    trackProgressPercentageEnd?: string;
    form?: UntypedFormGroup;
    private readonly unsubscribe = new Subject<void>();

    rangeInputLowestValueTimeout: any;
    rangeInputHighestValueTimeout: any;

    constructor(private cd: ChangeDetectorRef, private fb: UntypedFormBuilder) {}

    ngOnInit() {
        this.form = this.fb.group(
            {
                rangeOne: [this.controlledValStart || this.minVal],
                rangeTwo: [this.controlledValEnd || this.maxVal],
                rangeInputLowestValue: [
                    this.controlledValStart || this.minVal,
                    { validators: [Validators.required, Validators.pattern('^[0-9]*$')] },
                ], // whole numbers only
                rangeInputHighestValue: [this.controlledValEnd || this.maxVal, { validators: [Validators.required, Validators.pattern('^[0-9]*$')] }], // whole numbers only
            },
            {
                updateOn: 'change',
            }
        );

        this.form.controls['rangeInputLowestValue'].valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((inputVal: string) => {
            this.rangeInputLowestValueTimeout = setTimeout(() => {
                const newVal = parseInt(inputVal);

                const rangeOneVal = this.form?.controls['rangeOne'].value;
                const rangeTwoVal = this.form?.controls['rangeTwo'].value;

                const currLow = rangeOneVal > rangeTwoVal ? 'rangeTwo' : 'rangeOne';
                const currHigh = rangeOneVal > rangeTwoVal ? 'rangeOne' : 'rangeTwo';
                this.form?.controls[currLow].setValue(newVal);

                const highestInput = this.form?.controls['rangeInputHighestValue'];
                let highestInputValue = parseInt(highestInput?.value);

                if (newVal > highestInputValue) {
                    highestInputValue = newVal + this.inputStepSize > this.maxVal ? this.maxVal : newVal + this.inputStepSize;
                    highestInput?.setValue(highestInputValue);
                    this.form?.controls[currHigh].setValue(highestInputValue);
                }

                this.updateTrack(newVal, highestInputValue);

                this.cd.markForCheck();
            });
        });

        this.form.controls['rangeInputHighestValue'].valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((inputVal: string) => {
            this.rangeInputHighestValueTimeout = setTimeout(() => {
                const newVal = parseInt(inputVal);

                if (newVal > this.maxVal) {
                    this.form?.controls['rangeInputHighestValue'].setValue(this.maxVal);
                } else {
                    const rangeOneVal = this.form?.controls['rangeOne'].value;
                    const rangeTwoVal = this.form?.controls['rangeTwo'].value;

                    const currLow = rangeOneVal > rangeTwoVal ? 'rangeTwo' : 'rangeOne';
                    const currHigh = rangeOneVal > rangeTwoVal ? 'rangeOne' : 'rangeTwo';
                    this.form?.controls[currHigh].setValue(newVal);

                    const lowestInput = this.form?.controls['rangeInputLowestValue'];
                    let lowestInputValue = parseInt(lowestInput?.value);

                    if (newVal < lowestInputValue) {
                        lowestInputValue = newVal - this.inputStepSize < 0 ? 0 : newVal - this.inputStepSize;
                        lowestInput?.setValue(lowestInputValue);
                        this.form?.controls[currLow].setValue(lowestInputValue);
                    }

                    this.updateTrack(lowestInputValue, newVal);
                    this.cd.markForCheck();
                }
            });
        });

        this.handleRangeChange();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes?.controlledValStart?.currentValue !== undefined) {
            this.form?.controls['rangeInputLowestValue'].setValue(changes?.controlledValStart?.currentValue);
        }
        if (changes?.controlledValEnd?.currentValue !== undefined) {
            this.form?.controls['rangeInputHighestValue'].setValue(changes?.controlledValEnd?.currentValue);
        }
    }

    handleRangeChange() {
        let currLow = this.form?.controls['rangeOne'].value;
        let currHigh = this.form?.controls['rangeTwo'].value;

        if (currLow > currHigh) {
            const tmp = currHigh;
            currHigh = currLow;
            currLow = tmp;
        }

        this.updateTrack(currLow, currHigh);

        this.form?.controls['rangeInputLowestValue'].setValue(currLow, { emitEvent: false });
        this.form?.controls['rangeInputHighestValue'].setValue(currHigh, { emitEvent: false });
    }

    updateTrack(start: number, end: number) {
        this.trackProgressPercentageStart = `${Math.round(((start - (this.minVal || 0)) / ((this.maxVal || 0) - (this.minVal || 0))) * 100)}`;
        this.trackProgressPercentageEnd = `${100 - Math.round(((end - (this.minVal || 0)) / ((this.maxVal || 0) - (this.minVal || 0))) * 100)}`;
    }

    handleRangeSubmit(val?: ITickmark) {
        this.onSubmit();
    }

    onSubmit() {
        if (this.form?.invalid || this.form?.disabled) {
            FormService.markControlsAsTouched(this.form);
            return;
        }
        const { rangeInputLowestValue: from, rangeInputHighestValue: to }: { [key: string]: string } = this.form?.value;
        this.handleChange.emit({ from: parseInt(from), to: parseInt(to) });
    }

    ngOnDestroy() {
        clearTimeout(this.rangeInputLowestValueTimeout);
        clearTimeout(this.rangeInputHighestValueTimeout);
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
